<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { mdiClose } from '@mdi/js'
import { cloneDeep, isEqual } from 'lodash'
import { toKebabCase } from '@/shared/utils/format'
import { useValidation, type ValidationRule } from '@/shared/composables/use-validation'
import type { Validate } from '@/shared/types/validation.types'

import BaseButton from '@/shared/components/button/BaseButton.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue'
import SkeletonLoader from '../loader/SkeletonLoader.vue'
import LabelField from '../label/LabelField.vue'
import BaseDropdownMenu from '../dropdown/BaseDropdownMenu.vue'

interface Props {
  items: any[]
  itemText: string
  itemKey?: any
  label?: string
  placeholder?: string
  type?: ValidationRule
  max?: number
  required?: boolean
  loading?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'change'): void
  (e: 'clear'): void
  (e: 'validate', validation: Validate): void
}

defineOptions({ inheritAttrs: false })
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<any>({ required: true })

const name = toKebabCase(props.label || '')
const isActive = ref<boolean>(false)
const isTyping = ref<boolean>(false)
const original = ref<string>('')
const filter = ref<string>('')

const { validation, markDirty } = useValidation(
  model,
  props.type,
  props.required,
  props.max,
  (validation) => emits('validate', validation),
)

const filteredItems = computed((): any[] => {
  if (!isActive.value) return []
  if (!isTyping.value) return props.items

  return props.items.filter((item: any) =>
    item[props.itemText]?.toLowerCase().includes(filter.value.toLowerCase()),
  )
})

const toggleActive = (): void => {
  isActive.value = !isActive.value
}

const findItemByValue = (value: any): any | undefined => {
  if (!value) return undefined
  if (props.itemKey) return props.items.find((item) => item[props.itemKey] === value)
  else return props.items.find((item) => isEqual(item, value))
}

const onInput = (event: Event): void => {
  filter.value = (event.target as HTMLInputElement).value
  isTyping.value = true
  isActive.value = true
}

const onSelect = (item: any): void => {
  model.value = props.itemKey ? item[props.itemKey] : cloneDeep(item)
  filter.value = item[props.itemText]
  original.value = item[props.itemText]
  isActive.value = false
  isTyping.value = false
  emits('change')
}

const onMenuClose = (): void => {
  isActive.value = false
  if (model.value) {
    filter.value = original.value
  }
}

const onClear = (): void => {
  filter.value = ''
  original.value = ''
  model.value = undefined
  isTyping.value = false
  emits('clear')
}

const openMenu = (): void => {
  filter.value = ''
  isActive.value = !isActive.value
}

watch(
  model,
  (newValue) => {
    const selected = findItemByValue(newValue)
    if (selected) {
      filter.value = selected[props.itemText]
      original.value = selected[props.itemText]
    } else filter.value = ''
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="loading" class="w-full">
    <SkeletonLoader v-if="label" class="h-3.5 w-1/2 mb-1.5 mt-1" />
    <SkeletonLoader class="h-9.5 w-full" />
  </div>
  <div v-else class="relative" v-bind="$attrs">
    <LabelField v-if="label" :id="name" :text="label" :required="required" :validate="validation" />
    <div
      class="flex items-center border focus-within:ring"
      :class="[
        disabled ? 'border-dashed border-gray-300' : 'border-gray-500',
        validation?.isDirty && validation?.isInvalid
          ? 'animate-shake focus-within:bg-error/10 focus-within:border-error focus-within:ring-error'
          : 'focus-within:border-success focus-within:ring-success focus-within:bg-success/10 dark:focus-within:border-success  dark:focus-within:ring-success',
      ]"
      v-on-click-outside="onMenuClose"
    >
      <input
        v-model="filter"
        :id="name"
        type="text"
        autocomplete="off"
        spellcheck="false"
        :placeholder="placeholder"
        class="flex-1 text-sm truncate opacity-100 p-2 outline-none bg-transparent dark:text-white disabled:bg-gray-100 dark:disabled:bg-black dark:disabled:text-gray-400"
        :title="label"
        :disabled="disabled"
        @input="onInput"
        @blur="markDirty"
        @click="openMenu"
      />
      <div v-if="!disabled" class="flex items-center px-2 gap-1 shrink-0">
        <BaseButton v-if="model && filter" :title="$t('common.clear')" @click.stop="onClear">
          <SvgIcon class="fill-gray-700 dark:fill-white w-5 h-5" :path="mdiClose" />
        </BaseButton>
        <BaseButton
          v-if="!disabled"
          :title="$t('common.open')"
          :disabled="disabled"
          @click.stop="toggleActive"
        >
          <CaretIcon
            :active="isActive"
            :disabled="disabled"
            :validation="validation"
            class="w-5 h-5"
          />
        </BaseButton>
      </div>
    </div>
    <FadeTransition>
      <BaseDropdownMenu :open="filteredItems.length > 0 && isActive">
        <ul>
          <li
            v-for="(item, index) in filteredItems"
            :key="index"
            :title="item[itemText]"
            class="text-sm truncate dark:text-gray-300 hover:bg-gray-100 hover:dark:text-white dark:hover:bg-gray-800 cursor-pointer p-2"
            @click="onSelect(item)"
          >
            {{ item[itemText] }}
          </li>
        </ul>
      </BaseDropdownMenu>
    </FadeTransition>
  </div>
</template>
